import classNames from "classnames";
import React, { FunctionComponent, useMemo } from "react";
import { LightDarkVariant, LogoRenderer, NavMenuItem } from "../types";
import {
  GlobalHeaderButtonProps,
  GlobalHeaderProps,
  GlobalHeaderUtilityHeaderIconLinkProps,
  GlobalHeaderUtilityHeaderLinkProps,
} from "./global-header";
import { CSS_CLASSES } from "./global-header-css-classes";
import { GlobalHeaderCTAButtons } from "./global-header-cta-buttons";
import { GlobalHeaderNavMenu } from "./global-header-nav-menu";
import { GlobalHeaderUserMenu } from "./global-header-user-menu";

export interface GlobalHeaderMainBarProps {
  variant: LightDarkVariant;
  menuColor?: "interactive" | "primary";
  logoRenderer: LogoRenderer;
  navMenuItems: NavMenuItem[];
  openDesktopNavSubMenuOnHover: boolean;
  onNavMenuItemActivate: (id: string) => void;
  utilityHeaderLinks?: GlobalHeaderUtilityHeaderLinkProps[];
  utilityHeaderIconLinks?: GlobalHeaderUtilityHeaderIconLinkProps[];
  ctaButtons: GlobalHeaderButtonProps[];
  isUserSignedIn: boolean;
  userMenuProps?: GlobalHeaderProps["userMenuProps"];
  constrainContentWidth?: boolean;
  isDesktop: boolean;
}

export const GlobalHeaderMainBar: FunctionComponent<GlobalHeaderMainBarProps> = ({
  variant,
  menuColor,
  logoRenderer,
  navMenuItems,
  openDesktopNavSubMenuOnHover,
  onNavMenuItemActivate,
  utilityHeaderLinks,
  utilityHeaderIconLinks,
  ctaButtons,
  isUserSignedIn,
  userMenuProps,
  constrainContentWidth,
  isDesktop,
}) => {
  const rootClassName = useMemo(
    () =>
      classNames(CSS_CLASSES.GLOBAL_HEADER_MAIN_ROOT, {
        [CSS_CLASSES.GLOBAL_HEADER_MAIN_LIGHT]: variant === "light",
        [CSS_CLASSES.GLOBAL_HEADER_MAIN_DARK]: variant === "dark",
      }),
    [variant]
  );

  const contentClassName = useMemo(
    () =>
      classNames(CSS_CLASSES.GLOBAL_HEADER_MAIN_CONTENT, {
        "container mx-auto": constrainContentWidth,
      }),
    [constrainContentWidth]
  );

  const mainHeaderLeftSectionClassName = useMemo(
    () =>
      classNames(CSS_CLASSES.GLOBAL_HEADER_MAIN_LEFT_SECTION, {
        [CSS_CLASSES.GLOBAL_HEADER_MAIN_LEFT_SECTION_REVERSE]: isDesktop && navMenuItems.length > 0,
      }),
    [isDesktop, navMenuItems]
  );

  const ctaButtonsToRender = useMemo(() => {
    return ctaButtons.filter((b) => !isUserSignedIn || !b.hideWhenSignedIn);
  }, [ctaButtons, isUserSignedIn]);

  const maybeLogo = useMemo(
    () =>
      typeof logoRenderer === "function" ? (
        <div
          className={classNames(CSS_CLASSES.GLOBAL_HEADER_LOGO, {
            [CSS_CLASSES.GLOBAL_HEADER_LOGO_DESKTOP]: isDesktop,
            [CSS_CLASSES.GLOBAL_HEADER_LOGO_MOBILE]: !isDesktop,
          })}
        >
          {logoRenderer(variant, isDesktop)}
        </div>
      ) : null,
    [isDesktop, logoRenderer, variant]
  );

  const maybeUserMenu = useMemo(
    () =>
      isUserSignedIn &&
      userMenuProps && (
        <GlobalHeaderUserMenu
          {...userMenuProps}
          variant={userMenuProps.variant ?? (variant === "dark" ? "light" : "dark")}
          color={userMenuProps.color ?? (variant === "dark" ? "primary" : menuColor)}
          isDesktop={isDesktop}
        />
      ),
    [isDesktop, isUserSignedIn, menuColor, userMenuProps, variant]
  );

  return (
    <div className={rootClassName}>
      <div className={contentClassName}>
        <div className={mainHeaderLeftSectionClassName}>
          <GlobalHeaderNavMenu
            variant={variant}
            color={variant === "dark" ? "primary" : menuColor}
            menuItems={navMenuItems}
            openSubMenuOnHover={openDesktopNavSubMenuOnHover}
            onMenuItemActivate={onNavMenuItemActivate}
            links={utilityHeaderLinks}
            iconLinks={utilityHeaderIconLinks}
            ctaButtons={ctaButtonsToRender}
            isUserSignedIn={isUserSignedIn}
            isDesktop={isDesktop}
          />
          {maybeLogo}
        </div>
        <div className={CSS_CLASSES.GLOBAL_HEADER_MAIN_RIGHT_SECTION}>
          <GlobalHeaderCTAButtons ctaButtons={ctaButtonsToRender} isDesktop={isDesktop} />
          {maybeUserMenu}
        </div>
      </div>
    </div>
  );
};
